package io.horion.cv.pages.dashboard

import androidx.compose.runtime.*
import io.horion.cv.components.auth.RequireAuth
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaEllipsisVertical
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import io.horion.cv.api.datasource.common.Generate
import io.horion.cv.components.layouts.AppResponsiveLayout
import io.horion.cv.components.shared.CardDropdownMenu
import io.horion.cv.components.shared.CreateDocumentCard
import io.horion.cv.components.shared.DocumentCard
import io.horion.cv.di.koinGet
import io.horion.cv.models.ViewMode
import io.horion.cv.pages.navigation.Screen
import io.horion.cv.styles.SpanStyleCSS
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.utils.Constants.UBUNTU
import io.horion.cv.viewModel.DashboardViewModel
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

@Page("Lettres")
@OptIn(DelicateApi::class)
@Composable
fun LettresDeMotivationPage() = RequireAuth {

    val ctx = rememberPageContext()
    val dashboardViewModel = koinGet<DashboardViewModel>()

    var viewMode by remember { mutableStateOf(ViewMode.GRID) }

    LaunchedEffect(Unit) {
        dashboardViewModel.loadLetterHistory(refresh = true)
    }

    AppResponsiveLayout(activeLabel = "Lettres de motivation") {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .gap(24.px)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                SpanText(
                    text = "Lettres de motivation",
                    modifier = SpanStyleCSS.toModifier()
//                        .fontSize(if (isMobile) 20.px else 26.px)
                        .fontFamily(UBUNTU)
                        .fontWeight(FontWeight.SemiBold)
                        .color(Colors.DarkBlue)
                )
                ViewModeToggle(
                    current = viewMode,
                    onSelect = { viewMode = it }
                )
            }

            when (viewMode) {
                ViewMode.GRID -> LetterGridFull(
                    letterList = dashboardViewModel.letterList,
                    onNew = { ctx.router.navigateTo(Screen.Motivation.path) },
                    onEdit = { letter ->
                        ctx.router.navigateTo("${Screen.UpdateLetter.path}?id=${letter.uuidDocument}&model=${letter.model ?: ""}")
                    },
                    onDelete = { id ->
                        if (window.confirm("Voulez-vous vraiment supprimer cette lettre ?")) {
                            dashboardViewModel.deleteLetter(id)
                        }
                    },
                    onDownload = { url -> dashboardViewModel.downloadCv(url) },
                )
                ViewMode.LIST -> LetterListFull(
                    letterList = dashboardViewModel.letterList,
                    onNew = { ctx.router.navigateTo(Screen.Motivation.path) },
                    onEdit = { letter ->
                        ctx.router.navigateTo("${Screen.UpdateLetter.path}?id=${letter.uuidDocument}&model=${letter.model ?: ""}")
                    },
                    onDelete = { id ->
                        if (window.confirm("Voulez-vous vraiment supprimer cette lettre ?")) {
                            dashboardViewModel.deleteLetter(id)
                        }
                    },
                    onDownload = { url -> dashboardViewModel.downloadCv(url) }
                )
            }
        }
    }
}

@Composable
fun LetterGridFull(
    letterList: List<Generate>,
    onNew: () -> Unit,
    onEdit: (Generate) -> Unit = {},
    onDelete: (String) -> Unit,
    onDownload: (String) -> Unit,
) {

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .gap(20.px)
            .styleModifier { property("flex-wrap", "wrap") },
        verticalAlignment = Alignment.Top
    ) {
        CreateDocumentCard(
            text = "Créer une nouvelle lettre\nde motivation",
            onClick = onNew
        )
        letterList.forEach { letter ->
            DocumentCard(
                title = letter.name ?: "Lettre sans titre",
                modifiedAgo = letter.date,
                previewSrc = letter.thumbnailUrl.ifBlank { null },
                onEdit = { onEdit(letter) },
                onDelete = { onDelete(letter.id) },
                onDownload = { onDownload(letter.url) }
            )
        }
    }
}

@Composable
fun LetterListFull(
    letterList: List<Generate>,
    onNew: () -> Unit,
    onEdit: (Generate) -> Unit = {},
    onDelete: (String) -> Unit,
    onDownload: (String) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .gap(8.px)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(56.px)
                .backgroundColor(Colors.White)
                .border(1.px, LineStyle.Solid, Colors.lineBorder)
                .borderRadius(8.px)
                .padding(leftRight = 20.px)
                .cursor(Cursor.Pointer)
                .onClick { onNew() }
                .gap(12.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = "+",
                modifier = Modifier
                    .fontSize(20.px)
                    .color(Colors.Graytext)
                    .fontWeight(FontWeight.Light)
            )
            SpanText(
                text = "Créer une nouvelle lettre de motivation",
                modifier = Modifier
                    .fontFamily(INTER)
                    .fontSize(15.px)
                    .color(Colors.Graytext)
            )
        }

        letterList.forEach { letter ->
            LetterListRow(
                letter = letter,
                onDelete = { onDelete(letter.id) },
                onDownload = { onDownload(letter.url) }
            )
        }
    }
}

@Composable
fun LetterListRow(
    letter: Generate,
    onDelete: () -> Unit,
    onDownload: () -> Unit
) {
    var showMenu by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(64.px)
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, Colors.lineBorder)
            .borderRadius(8.px)
            .padding(leftRight = 20.px),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(
            modifier = Modifier.gap(14.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .width(36.px).height(48.px)
                    .backgroundColor(Colors.PriceGray)
                    .borderRadius(4.px)
                    .border(1.px, LineStyle.Solid, Colors.lineBorder)
                    .overflow(Overflow.Hidden)
            ) {
                if (letter.thumbnailUrl.isNotBlank()) {
                    Image(src = letter.thumbnailUrl, description = letter.name ?: "Lettre", modifier = Modifier.fillMaxWidth())
                }
            }

            Column(modifier = Modifier.gap(4.px)) {
                SpanText(
                    text = letter.name ?: "Lettre sans titre",
                    modifier = Modifier
                        .fontFamily(INTER)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.Medium)
                        .color(Colors.Black)
                )
                SpanText(
                    text = letter.date,
                    modifier = Modifier
                        .fontFamily(INTER)
                        .fontSize(12.px)
                        .color(Colors.Graytext)
                )
            }
        }

        Box(
            modifier = Modifier
                .size(32.px)
                .borderRadius(6.px)
                .cursor(Cursor.Pointer)
                .onClick { showMenu = !showMenu },
            contentAlignment = Alignment.Center
        ) {
            /*Image(
                src = "/dots-vertical.svg",
                description = "Options",
                modifier = Modifier.size(20.px)
            )*/
            FaEllipsisVertical(
                modifier = Modifier.fontSize(20.px)
                    .styleModifier { property("filter", "none")}

            )
        }

        if (showMenu) {
            CardDropdownMenu(
                onEdit = { showMenu = false },
                onDelete = { showMenu = false; onDelete() },
                onDownload = { showMenu = false; onDownload() },
                onDismiss = { showMenu = false }
            )
        }
    }
}
